import type { QuoteKind } from "@townsy/contracts";
import { isApiError } from "../api/client";

/**
 * Every error the dollar-commerce routes can return (docs/06 section 4, ADR-0007), in plain
 * language, with what the checkout should do next. The client branches on `code`, never on the
 * server's text; the copy always says what happened to the visitor's money ("nothing was charged")
 * only when that is certain from the code.
 */

/** What the checkout offers after a failure. */
export type FailureAction =
  /** Resend the SAME request (same Idempotency-Key / same quote): network, 5xx, provider timeout, rate limit. */
  | "retry"
  /** Ask the server for a fresh price (new quote, new key): expired / stale quote, version changed. */
  | "requote"
  /** Re-read the order from the server and show its real state. */
  | "refresh_order"
  /** Nothing to retry here (self takeover, sign-in needed, limit reached...). */
  | "close"
  /** ADR-0046: ask for (or correct) the buyer's country and postal code, then price again with sales tax. */
  | "address";

export interface CommerceFailure {
  code: string;
  message: string;
  action: FailureAction;
  /** Listings, prices, balances or the property are stale: refetch them. */
  refresh: boolean;
}

export type CommerceStage = "quote" | "order" | "payment_intent" | "cancel" | "order_read";

const KIND_NOUN: Record<QuoteKind, string> = {
  COMMERCIAL_ACQUISITION: "building",
  RESIDENTIAL_PLOT: "plot",
  HOME_UPGRADE: "upgrade",
  CATALOG_ITEM: "item",
  BILLBOARD_RENTAL: "billboard rental",
  /** M6: card funding of the buyer's own sponsored-task campaign budget (never Townsy Credit). */
  CAMPAIGN_FUNDING: "campaign budget",
};

/** Detects the funding refusal "card part would be below Stripe's 50-cent minimum" (VALIDATION_FAILED). */
export function isBelowCardMinimum(err: { message?: string; details?: unknown }): boolean {
  const hay = `${err.message ?? ""} ${safeJson(err.details)}`.toLowerCase();
  return hay.includes("below_card_minimum") || hay.includes("below card minimum");
}

function safeJson(v: unknown): string {
  try {
    return v === undefined ? "" : JSON.stringify(v);
  } catch {
    return "";
  }
}

/** PROPERTY_RESERVED: someone else's (or another of your own) checkout holds the property right now. */
export function reservedMessage(kind: QuoteKind | null): string {
  switch (kind) {
    case "COMMERCIAL_ACQUISITION":
      return "Another member is checking out this building right now. Nothing was charged. Their hold ends within 10 minutes; if they don't complete the purchase, you can try again.";
    case "RESIDENTIAL_PLOT":
      return "Another member is checking out this plot right now. Nothing was charged. Their hold ends within 10 minutes; if they don't complete the purchase, you can try again.";
    case "HOME_UPGRADE":
      return "Another checkout is holding your home right now (for example an unfinished card payment in another tab). Nothing was charged. Finish or cancel it in My orders, or wait for its hold to end.";
    default:
      return "This property is held by another checkout right now. Nothing was charged. Try again in a few minutes.";
  }
}

export const NETWORK_FAILURE_MESSAGE = "Couldn't reach Townsy. Check your connection and try again: a repeated attempt can never charge you twice.";

export function describeCommerceError(err: unknown, opts: { stage: CommerceStage; kind?: QuoteKind | null } = { stage: "quote" }): CommerceFailure {
  const noun = opts.kind ? KIND_NOUN[opts.kind] : "purchase";
  if (!isApiError(err)) {
    return { code: "NETWORK", message: NETWORK_FAILURE_MESSAGE, action: opts.stage === "order_read" ? "refresh_order" : "retry", refresh: false };
  }
  const fail = (message: string, action: FailureAction, refresh = false): CommerceFailure => ({ code: err.code, message, action, refresh });
  if (opts.kind === "CAMPAIGN_FUNDING") {
    const funding = campaignFundingFailure(err.code, err.message, opts.stage, fail);
    if (funding) return funding;
  }
  switch (err.code) {
    case "QUOTE_EXPIRED":
      return fail("This price expired (prices are held for two minutes). Nothing was charged. Get a fresh price to continue.", "requote");
    case "QUOTE_STALE":
      return fail(
        "The price, the owner or your available Townsy Credit changed since this price was made. Nothing was charged. Review the new price.",
        "requote",
        true,
      );
    case "PROPERTY_VERSION_CHANGED":
      return fail("This property changed (a new owner or a new price) since you looked at it. Nothing was charged. Review the new price.", "requote", true);
    case "PRICE_CHANGED":
      return fail("The price changed since you opened this. Nothing was charged. Review the new price.", "requote", true);
    case "PROPERTY_RESERVED":
      return fail(reservedMessage(opts.kind ?? null), "close", true);
    case "SELF_TAKEOVER":
      return fail("You already own this building. You cannot buy your own building: it would only raise its price. Nothing was charged.", "close", true);
    case "RESERVATION_LIMIT":
      return fail(
        "You already have a card checkout in progress. Finish or cancel it first (see My orders); it ends by itself when its hold runs out.",
        "close",
      );
    case "ORDER_NOT_PAYABLE":
      return fail(
        opts.stage === "cancel"
          ? "This order can no longer be canceled (it is already complete or closed). Showing its current state."
          : "This order can no longer take a payment (it expired, was canceled or is already complete). Showing its current state.",
        "refresh_order",
      );
    case "PAYMENT_PROVIDER_ERROR":
      return fail("The payment service didn't answer in time. Nothing was charged twice: try again.", "retry");
    case "TAX_ADDRESS_REQUIRED":
      // The server's text is specific and safe ("Enter your ZIP code."); nothing is priced or charged without it.
      return fail(`${err.message || "Enter your country and postal code so sales tax can be added."} Nothing was charged.`, "address");
    case "TAX_CALCULATION_FAILED":
      return fail("Sales tax could not be calculated right now, so no price was fixed. Nothing was charged. Try again in a moment.", "requote");
    case "IDEMPOTENCY_CONFLICT":
      return fail("This confirmation clashed with an earlier request. Nothing extra was charged. Please start again with a fresh price.", "requote", true);
    case "PROPERTY_TYPE_NOT_ELIGIBLE":
      return fail(
        opts.kind === "COMMERCIAL_ACQUISITION"
          ? "Only commercial buildings can be bought or taken over. Homes can never be taken over."
          : "This property can't be bought this way. Homes can never be taken over by other players.",
        "close",
      );
    case "PROPERTY_NOT_AVAILABLE":
      return fail(`Someone else bought this ${noun} first. Nothing was charged.`, "close", true);
    case "PLOT_LIMIT_REACHED":
      return fail(
        "You already own a plot, or one is being held for your checkout. Each account can own one plot. Nothing was charged.",
        "close",
        true,
      );
    case "STAGE_NOT_NEXT":
      return fail("Your home is no longer at the level you saw (it may already be upgraded). Nothing was charged. Check the next level.", "close", true);
    case "HOME_REQUIRED":
      return fail("Buy a plot first at Townsy Real Estate, 1 Plaza Way. Nothing was charged.", "close", true);
    case "FEATURE_DISABLED":
      return fail(
        opts.kind === "COMMERCIAL_ACQUISITION"
          ? "Buying and taking over stores is switched off right now. Nothing was charged."
          : opts.kind === "BILLBOARD_RENTAL"
            ? "Billboard rentals are switched off right now. Nothing was charged."
            : "Card and Townsy Credit purchases are switched off right now. Nothing was charged.",
        "close",
        true,
      );
    case "BILLBOARD_DATES_UNAVAILABLE":
      return fail(
        "At least one of those days was just held or booked by someone else. Nothing was charged. Pick other dates from the refreshed calendar.",
        "close",
        true,
      );
    case "CREATIVE_NOT_APPROVED":
      return fail(
        "That billboard design is not approved (it may be pending, rejected or revoked). Nothing was charged. Choose an approved billboard design.",
        "close",
        true,
      );
    case "CATEGORY_RESTRICTED":
      return fail("Your advertiser category is restricted until Townsy's advertiser policy allows it. Nothing was charged.", "close");
    case "VALIDATION_FAILED":
      if (isBelowCardMinimum(err)) {
        return fail(
          "A card payment must be at least $0.50, so this price can only be paid entirely with Townsy Credit. Apply your credit if it covers the price. Nothing was charged.",
          "close",
        );
      }
      return fail(`The request was rejected: ${err.message || "invalid request"}. Nothing was charged.`, "close", true);
    case "UNAUTHENTICATED":
      return fail("Sign in to continue. Your session may have expired. Nothing was charged.", "close");
    case "EMAIL_NOT_VERIFIED":
      return fail("Verify your email address before paying with a card or Townsy Credit.", "close");
    case "FORBIDDEN":
    case "BANNED":
      return fail("Your account can't make purchases right now.", "close");
    case "NOT_FOUND":
      return fail(
        opts.stage === "order_read" || opts.stage === "cancel" || opts.stage === "payment_intent"
          ? "This order was not found."
          : opts.kind === "BILLBOARD_RENTAL"
            ? "This billboard or design could not be found. Nothing was charged."
            : `This ${noun} is no longer for sale.`,
        "close",
        true,
      );
    case "RATE_LIMITED":
      return fail("Too many attempts. Wait a moment, then try again.", "retry");
    case "INSUFFICIENT_COINS":
      return fail("This purchase uses dollars, not coins. Please start again.", "close", true);
    case "DEPENDENCY_UNAVAILABLE":
    case "INTERNAL":
      return fail(
        opts.stage === "order_read"
          ? "Townsy couldn't load this order right now. Try again in a moment."
          : "Townsy couldn't confirm this step. Try again: a repeated attempt can never charge you twice.",
        opts.stage === "order_read" ? "refresh_order" : "retry",
      );
    default:
      return fail(err.message || "The purchase could not be completed.", err.retryable ? "retry" : "close");
  }
}

/**
 * Campaign funding (M6, ADR-0011 #2): card only, the owner's own campaign, only while they still own the
 * store. The server's explanation for a refused campaign is specific and safe ("This store changed owners:
 * its campaign can no longer be funded."), so it is shown with what happened to the money.
 */
function campaignFundingFailure(
  code: string,
  serverMessage: string,
  stage: CommerceStage,
  fail: (message: string, action: FailureAction, refresh?: boolean) => CommerceFailure,
): CommerceFailure | null {
  const order = stage === "order_read" || stage === "cancel" || stage === "payment_intent";
  switch (code) {
    case "CAMPAIGN_STATE_INVALID":
      return fail(`${serverMessage || "This campaign can no longer be funded."} Nothing was charged.`, "close", true);
    case "NOT_FOUND":
      return order ? null : fail("This campaign was not found among your campaigns. Nothing was charged.", "close", true);
    case "FORBIDDEN":
      return fail("Only the campaign's owner can fund it, and only while they own the store. Nothing was charged.", "close", true);
    case "VALIDATION_FAILED":
      return fail(
        `The amount was not accepted: ${serverMessage || "invalid amount"}. Campaign budgets are funded by card only, between $5.00 and $10,000.00 per payment. Nothing was charged.`,
        "close",
      );
    case "FEATURE_DISABLED":
      return fail("Card payments for campaign budgets are switched off right now. Nothing was charged.", "close", true);
    case "EMAIL_NOT_VERIFIED":
      return fail("Verify your email address before funding a campaign by card. Nothing was charged.", "close");
    default:
      return null;
  }
}
